/**
 * Session Coordinator State Stores
 *
 * Manages state for the SessionCoordinator.
 */

import { makeStateStore } from '@playcastdotio/StateStore';
import type { HostBroadcastStatus } from '@playcastdotio/CoreTypes';

/**
 * Session Presence State (Dynamic)
 *
 * Tracks the current session state that changes during runtime.
 */
export type SessionPresenceState = {
  status: 'offline' | 'idle' | 'busy' | 'hosting';
  playerCount: number;
  hostPeerId: string | undefined;
  remoteHostPeerId: string | undefined;
  runningGame: { type: 'steam' | 'epic' | 'microsoft' | 'exe'; id: string } | undefined; // TODO: Remove in future (Phase 2)
  lastUpdated: number;
  adminSessionActive: boolean;
};

/**
 * Static Host Properties (Set Once on Init)
 *
 * These values rarely change and are calculated once at startup.
 */
export type StaticHostProperties = {
  guestAccount: boolean;
  isCloudHost: boolean;
  geohash: string | undefined;
  coords: { lat: number; lng: number } | undefined;
};

/**
 * Host Broadcast State (Replicated to node-service)
 *
 * Subset of HostState fields relevant to broadcast lifecycle.
 * Replicates on the 'hostState' channel so the node-service can
 * merge it with edition data from GameLaunchCoordinator.
 */
export type HostBroadcastState = {
  status: HostBroadcastStatus;
};

/**
 * Combined Session State
 */
export type SessionCoordinatorState = {
  presence: SessionPresenceState;
  staticProperties: StaticHostProperties;
  broadcast: HostBroadcastState;
};

/**
 * Session Stores
 */
export type SessionStores = {
  presence: ReturnType<typeof makeStateStore<SessionPresenceState>>;
  staticProperties: ReturnType<typeof makeStateStore<StaticHostProperties>>;
  broadcast: ReturnType<typeof makeStateStore<HostBroadcastState>>;
};

/**
 * Create session stores
 */
export function createSessionStores(): SessionStores {
  return {
    presence: makeStateStore<SessionPresenceState>({
      status: 'idle', // Match legacy: hosts start as 'idle', not 'offline'
      playerCount: 0,
      hostPeerId: undefined,
      remoteHostPeerId: undefined,
      runningGame: undefined,
      lastUpdated: Date.now(),
      adminSessionActive: false,
    }),
    staticProperties: makeStateStore<StaticHostProperties>({
      guestAccount: false,
      isCloudHost: false,
      geohash: undefined,
      coords: undefined,
    }),
    broadcast: makeStateStore<HostBroadcastState>({
      status: 'idle',
    }, {
      replicate: { channel: 'hostState', targets: ['nodeService'] },
    }),
  };
}
